import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Pill, WordTitle } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";
import { Tween } from "@/components/site/Journey";
import ShaderBackground from "@/components/site/ShaderBackground";
import Radar from "@/components/site/Radar";
import ScorecardResult from "@/components/site/ScorecardResult";
import {
  MAX_SCORE,
  decode,
  encode,
  getScorecard,
  total,
} from "@/lib/scorecard";

const EASE = [0.15, 0.9, 0.34, 0.95];
const COUNT = 7;
const LAST = COUNT - 1;
const FOCUS_STEP = COUNT;

function Progress({ step, answers, onJump, questions }) {
  return (
    <div className="flex gap-1.5" role="list" aria-label="Progress">
      {questions.map((q, i) => {
        const done = answers[i] !== null && answers[i] !== undefined;
        return (
          <button
            key={q.area}
            type="button"
            role="listitem"
            disabled={!done && i !== step}
            onClick={() => onJump(i)}
            aria-label={`Question ${i + 1}: ${q.area}`}
            aria-current={i === step ? "step" : undefined}
            className="group/p flex-1 py-2 disabled:cursor-default"
          >
            <span className="block h-[3px] overflow-hidden rounded-full bg-white/15">
              <span
                className="block h-full origin-left rounded-full bg-white transition-transform duration-700"
                style={{
                  transform: `scaleX(${done || i === step ? 1 : 0})`,
                  opacity: i === step && !done ? 0.5 : 1,
                  transitionTimingFunction: "cubic-bezier(.15,.9,.34,.95)",
                }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Answer({ option, selected, onPick, index }) {
  return (
    <motion.button
      type="button"
      onClick={onPick}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.12 + index * 0.06, ease: EASE }}
      whileTap={{ scale: 0.985 }}
      aria-pressed={selected}
      data-testid={`answer-${option.key}`}
      className={`group flex w-full items-center gap-4 rounded-[20px] p-4 text-left ring-1 transition-colors duration-300 sm:p-5 ${
        selected
          ? "bg-white text-black ring-white"
          : "bg-white/[0.06] ring-white/10 hover:bg-white/[0.12] hover:ring-white/30"
      }`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-medium transition-colors duration-300 ${
          selected
            ? "bg-black text-white"
            : "bg-white/10 group-hover:bg-white/20"
        }`}
      >
        {option.key}
      </span>
      <span className="text-[16px] leading-[1.25] tracking-[-0.015em] sm:text-[19px]">
        {option.text}
      </span>
    </motion.button>
  );
}

function ReadinessCard({ answers, step, questions }) {
  const given = answers.filter((a) => a !== null);
  return (
    <div className="hidden rounded-[28px] bg-white/[0.04] p-6 ring-1 ring-white/10 lg:block">
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] uppercase tracking-[0.1em] text-white/45">
          Readiness map
        </span>
        <span className="nm-display text-[34px] leading-none tracking-[-0.03em]">
          <Tween value={total(given)} duration={600} />
          <span className="text-[16px] text-white/40"> / {MAX_SCORE}</span>
        </span>
      </div>
      <Radar
        values={answers}
        questions={questions}
        active={step < COUNT ? step : -1}
        className="mt-2 w-full text-white"
      />
      <p className="mt-1 text-center text-[13px] text-white/45">
        It fills in as you answer.
      </p>
    </div>
  );
}

export default function Scorecard() {
  const { vertical } = useParams();
  const card = getScorecard(vertical);
  return card ? (
    <Quiz key={card.id} card={card} />
  ) : (
    <Navigate to="/scorecard" replace />
  );
}

function Quiz({ card }) {
  const QUESTIONS = card.questions;
  const FOCUS = card.focus;
  const [params, setParams] = useSearchParams();
  const reduce = useReducedMotion();
  const fromUrl = decode(params.get("sc"));
  const [step, setStep] = useState(fromUrl ? FOCUS_STEP + 1 : -1);
  const [answers, setAnswers] = useState(
    () => fromUrl || Array(COUNT).fill(null),
  );
  const [focus, setFocus] = useState(params.get("f") || "");
  const lock = useRef(false);
  const timer = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);

  const finish = useCallback(
    (nextFocus) => {
      const next = { sc: encode(answers) };
      if (nextFocus) next.f = nextFocus;
      setParams(next, { replace: true });
      setStep(FOCUS_STEP + 1);
      window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
    },
    [answers, reduce, setParams],
  );

  const pick = useCallback(
    (index, points) => {
      if (lock.current) return;
      lock.current = true;
      setAnswers((a) => a.map((v, i) => (i === index ? points : v)));
      timer.current = window.setTimeout(
        () => {
          lock.current = false;
          setStep(index === LAST ? FOCUS_STEP : index + 1);
        },
        reduce ? 0 : 380,
      );
    },
    [reduce],
  );

  const back = useCallback(() => {
    if (lock.current) return;
    setStep((s) => Math.max(-1, s - 1));
  }, []);

  useEffect(() => {
    if (step < 0 || step > LAST) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      const index =
        "abcd".indexOf(key) >= 0 ? "abcd".indexOf(key) : "1234".indexOf(key);
      if (index >= 0 && index < 4) {
        e.preventDefault();
        pick(step, QUESTIONS[step].options[index].points);
      } else if (key === "arrowleft" || key === "backspace") {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, pick, back, QUESTIONS]);

  const restart = () => {
    setParams({}, { replace: true });
    setAnswers(Array(COUNT).fill(null));
    setFocus("");
    lock.current = false;
    setStep(-1);
    window.scrollTo({ top: 0 });
  };

  if (step > FOCUS_STEP)
    return (
      <ScorecardResult
        card={card}
        answers={answers}
        focus={focus}
        onRestart={restart}
      />
    );

  const question = QUESTIONS[step];
  return (
    <main className="relative min-h-screen overflow-hidden bg-black text-white">
      <ShaderBackground dark strength={0.22} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col px-5 pb-10 pt-28 sm:pt-32">
        <AnimatePresence mode="wait">
          {step === -1 && (
            <motion.section
              key="intro"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="grid flex-1 items-center gap-10 lg:grid-cols-12"
              data-testid="scorecard-intro"
            >
              <div className="lg:col-span-7">
                <Pill white>Agent-readiness scorecard · {card.label}</Pill>
                <WordTitle
                  text={card.headline}
                  className="mt-6 max-w-[900px] text-[44px] leading-[0.98] sm:text-[72px] lg:text-[84px]"
                />
                <p className="mt-8 max-w-[520px] text-[19px] leading-[1.3] tracking-[-0.015em] text-white/65 sm:text-[22px]">
                  {card.sub}
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <PillButton
                    variant="inverse"
                    size="lg"
                    onClick={() => setStep(0)}
                    data-testid="scorecard-start"
                  >
                    Start the scorecard
                  </PillButton>
                  <span className="text-[13px] text-white/45">
                    Instant result. No email needed.
                  </span>
                </div>
                <ul className="mt-12 flex flex-wrap gap-2 text-[12px] text-white/60">
                  {[
                    "Score out of 21",
                    "A plan for your next 90 days",
                    "Answers stay in your browser",
                  ].map((t) => (
                    <li
                      key={t}
                      className="rounded-full px-4 py-2 ring-1 ring-white/15"
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-[28px] bg-white/[0.04] p-6 ring-1 ring-white/10 sm:p-8">
                  <Radar
                    demo
                    className="mx-auto w-full max-w-[420px] text-white"
                  />
                </div>
              </div>
            </motion.section>
          )}

          {step >= 0 && step <= LAST && (
            <motion.section
              key={`q${step}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="grid flex-1 gap-10 lg:grid-cols-12"
              data-testid={`scorecard-q-${step}`}
            >
              <div className="lg:col-span-7">
                <Progress
                  questions={QUESTIONS}
                  step={step}
                  answers={answers}
                  onJump={(i) => i !== step && !lock.current && setStep(i)}
                />
                <div className="mt-8 flex items-center justify-between">
                  <Pill white>
                    {String(step + 1).padStart(2, "0")} / 0{QUESTIONS.length} ·{" "}
                    {question.area}
                  </Pill>
                  <span className="text-[12px] tabular-nums text-white/45 lg:hidden">
                    {total(answers.filter((a) => a !== null))} / {MAX_SCORE}
                  </span>
                </div>
                <h1 className="nm-title mt-6 max-w-[760px] text-[34px] leading-[1.02] sm:text-[52px]">
                  {question.q}
                </h1>
                <div className="mt-8 space-y-2.5">
                  {question.options.map((option, i) => (
                    <Answer
                      key={option.key}
                      index={i}
                      option={option}
                      selected={answers[step] === option.points}
                      onPick={() => pick(step, option.points)}
                    />
                  ))}
                </div>
                <div className="mt-8 flex items-center justify-between text-[13px] text-white/45">
                  <button
                    type="button"
                    onClick={back}
                    className="inline-flex items-center gap-2 transition-colors hover:text-white"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <span className="hidden sm:inline">
                    Press A–D or 1–4 to answer
                  </span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-28">
                  <ReadinessCard
                    answers={answers}
                    step={step}
                    questions={QUESTIONS}
                  />
                </div>
              </div>
            </motion.section>
          )}

          {step === FOCUS_STEP && (
            <motion.section
              key="focus"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="mx-auto flex w-full max-w-[860px] flex-1 flex-col justify-center"
              data-testid="scorecard-focus"
            >
              <Pill white className="w-fit">
                Optional · tailors your result
              </Pill>
              <h1 className="nm-title mt-6 text-[34px] leading-[1.02] sm:text-[56px]">
                {FOCUS.q}
              </h1>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {FOCUS.options.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={focus === f.id}
                    onClick={() => setFocus(focus === f.id ? "" : f.id)}
                    className={`rounded-full px-5 py-3 text-[15px] ring-1 transition-colors duration-300 ${
                      focus === f.id
                        ? "bg-white text-black ring-white"
                        : "bg-white/[0.06] ring-white/15 hover:bg-white/[0.14]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <PillButton
                  variant="inverse"
                  size="lg"
                  onClick={() => finish(focus)}
                  data-testid="scorecard-finish"
                >
                  See my result
                </PillButton>
                <button
                  type="button"
                  onClick={() => finish("")}
                  className="text-[13px] text-white/50 transition-colors hover:text-white"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={back}
                  className="ml-auto inline-flex items-center gap-2 text-[13px] text-white/45 transition-colors hover:text-white"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
